import React, { useEffect, useRef, useState, useCallback } from "react";

// ─── CONFIG ────────────────────────────────────────────────────────────────
const TILE  = 40;
const COLS  = 15;
const ROWS  = 13;
const W     = TILE * COLS;
const H     = TILE * ROWS;

// 1 = wall, 0 = path
const MAZE_TEMPLATE = [
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
  [1,0,0,0,0,0,1,0,1,0,0,0,0,0,1],
  [1,0,1,1,0,1,1,0,1,1,0,1,1,0,1],
  [1,0,1,0,0,0,0,0,0,0,0,0,1,0,1],
  [1,0,1,0,1,1,0,1,0,1,1,0,1,0,1],
  [1,0,0,0,1,0,0,0,0,0,1,0,0,0,1],
  [1,0,1,0,1,0,1,1,1,0,1,0,1,0,1],
  [1,0,1,0,0,0,0,0,0,0,0,0,1,0,1],
  [1,0,1,1,0,1,1,0,1,1,0,1,1,0,1],
  [1,0,0,0,0,0,1,0,1,0,0,0,0,0,1],
  [1,0,1,0,1,1,1,0,1,1,1,0,1,0,1],
  [1,0,0,0,0,0,0,0,0,0,0,0,0,0,1],
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
];

// ─── HELPERS ───────────────────────────────────────────────────────────────
const isWall = (x, y) => {
  if (y < 0 || y >= ROWS || x < 0 || x >= COLS) return true;
  return MAZE_TEMPLATE[y][x] === 1;
};

const getFreeCells = () => {
  const cells = [];
  for (let y = 0; y < ROWS; y++)
    for (let x = 0; x < COLS; x++)
      if (!isWall(x, y)) cells.push({ x, y });
  return cells;
};

const BFS = (startX, startY, goalX, goalY) => {
  if (startX === goalX && startY === goalY) return null;
  const queue = [{ x: startX, y: startY, path: [] }];
  const visited = new Set([`${startX},${startY}`]);
  const dirs = [[0,-1],[0,1],[-1,0],[1,0]];
  while (queue.length) {
    const { x, y, path } = queue.shift();
    for (const [dx, dy] of dirs) {
      const nx = x + dx, ny = y + dy;
      const key = `${nx},${ny}`;
      if (!isWall(nx, ny) && !visited.has(key)) {
        const newPath = [...path, { x: nx, y: ny }];
        if (nx === goalX && ny === goalY) return newPath[0] || null;
        visited.add(key);
        queue.push({ x: nx, y: ny, path: newPath });
      }
    }
  }
  return null;
};

// Sample dishes (replace with real Firebase data)
const SAMPLE_DISHES = [
  { id: "1", nome: "Arancino",       prezzo: "3.50", emoji: "🍊" },
  { id: "2", nome: "Pizza Margherita",prezzo: "7.00", emoji: "🍕" },
  { id: "3", nome: "Pasta al Nero",  prezzo: "9.00", emoji: "🍝" },
  { id: "4", nome: "Cannolo",        prezzo: "2.50", emoji: "🥐" },
  { id: "5", nome: "Caponata",       prezzo: "5.00", emoji: "🥗" },
];

// ─── DRAW HELPERS ──────────────────────────────────────────────────────────
const drawRoundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

// ─── COMPONENT ─────────────────────────────────────────────────────────────
export default function PiattoManGame({
  localeTag,
  tableId,
  tokenUrl,
  menuDishes = SAMPLE_DISHES,
  onBack,
}) {
  const canvasRef  = useRef(null);
  const stateRef   = useRef(null); // single mutable game state (no re-render lag)
  const rafRef     = useRef(null);
  const lastDanger = useRef(0);

  // React state only for HUD
  const [hud, setHud]         = useState({ score: 0, lives: 3, foods: 4 });
  const [phase, setPhase]     = useState("playing"); // playing | dead | win | gameover
  const [collected, setCollected] = useState(null); // { nome, prezzo, emoji } popup

  // ── Build initial state ──────────────────────────────────────────────────
  const buildState = useCallback(() => {
    const free = getFreeCells();
    const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);
    const shuffled = shuffle(free);

    // player at first free cell near top-left
    const playerStart = { x: 1, y: 1 };

    // pick food positions (far from player)
    const far = shuffled.filter(c => Math.abs(c.x - 1) + Math.abs(c.y - 1) > 5);
    const dishes = shuffle(menuDishes).slice(0, Math.min(4, menuDishes.length));
    const foods = dishes.map((d, i) => {
      const pos = far[i] || shuffled[i + 4];
      let nome = d.nome;
      if (typeof nome === "object") nome = nome.it || nome.en || "Piatto";
      return { x: pos.x, y: pos.y, nome, prezzo: d.prezzo, emoji: d.emoji || "🍽️", imageUrl: d.immagineUrl || "" };
    });

    // danger starts at bottom-right area
    const dangerStart = { x: COLS - 2, y: ROWS - 2 };

    return {
      player:  { ...playerStart, dir: [1, 0], mouth: 0, mouthOpen: true },
      dangers: [{ ...dangerStart }],
      foods,
      score:   0,
      lives:   3,
      phase:   "playing",
      moveDir: null,
      nextDir: null,
    };
  }, [menuDishes]);

  // ── Init ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    stateRef.current = buildState();
    setHud({ score: 0, lives: 3, foods: stateRef.current.foods.length });
    setPhase("playing");
  }, [buildState]);

  // ── Keyboard ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const map = {
      ArrowUp:    [0, -1], w: [0, -1], W: [0, -1],
      ArrowDown:  [0,  1], s: [0,  1], S: [0,  1],
      ArrowLeft:  [-1, 0], a: [-1, 0], A: [-1, 0],
      ArrowRight: [1,  0], d: [1,  0], D: [1,  0],
    };
    const onKey = (e) => {
      const d = map[e.key];
      if (!d || !stateRef.current || stateRef.current.phase !== "playing") return;
      e.preventDefault();
      stateRef.current.nextDir = d;
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ── Touch joystick ────────────────────────────────────────────────────────
  const startTouch = useRef(null);
  const handleTouchStart = (e) => { startTouch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const handleTouchEnd = (e) => {
    if (!startTouch.current || !stateRef.current) return;
    const dx = e.changedTouches[0].clientX - startTouch.current.x;
    const dy = e.changedTouches[0].clientY - startTouch.current.y;
    if (Math.abs(dx) > Math.abs(dy)) stateRef.current.nextDir = dx > 0 ? [1, 0] : [-1, 0];
    else stateRef.current.nextDir = dy > 0 ? [0, 1] : [0, -1];
  };

  // ── Game loop (rAF) ───────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let lastMove = 0;
    const MOVE_INTERVAL = 160; // ms per step

    const loop = (ts) => {
      rafRef.current = requestAnimationFrame(loop);
      const s = stateRef.current;
      if (!s || s.phase !== "playing") {
        draw(ctx, s);
        return;
      }

      // ── Move player ──────────────────────────────────────────────────────
      if (ts - lastMove > MOVE_INTERVAL) {
        lastMove = ts;

        // try queued direction first, fallback to current
        const tryDir = (dir) => {
          if (!dir) return false;
          const nx = s.player.x + dir[0], ny = s.player.y + dir[1];
          if (!isWall(nx, ny)) {
            s.player.dir = dir;
            s.player.x = nx;
            s.player.y = ny;
            return true;
          }
          return false;
        };

        if (!tryDir(s.nextDir)) tryDir(s.player.dir);
        s.nextDir = null;

        // mouth animation
        s.player.mouth = (s.player.mouth + 1) % 6;

        // ── Move danger via BFS ───────────────────────────────────────────
        if (ts - lastDanger.current > 600) {
          lastDanger.current = ts;
          s.dangers.forEach((d) => {
            const next = BFS(d.x, d.y, s.player.x, s.player.y);
            if (next) {
              d.x = next.x;
              d.y = next.y;
            }
          });
        }

        // ── Collect food ─────────────────────────────────────────────────
        const eaten = s.foods.find(f => f.x === s.player.x && f.y === s.player.y);
        if (eaten) {
          s.foods = s.foods.filter(f => f !== eaten);
          s.score += 10;
          setCollected(eaten);
          setTimeout(() => setCollected(null), 1500);
          setHud({ score: s.score, lives: s.lives, foods: s.foods.length });
          if (s.foods.length === 0) {
            // aggiungi un nuovo fantasma
            s.dangers.push({ x: COLS - 2, y: ROWS - 2 });

            // rigenera cibo
            const newState = buildState();

            s.foods = newState.foods;
            s.phase = "playing";

            setHud({
              score: s.score,
              lives: s.lives,
              foods: s.foods.length
            });
          }
        }

        // ── Danger collision ─────────────────────────────────────────────
          const hit = s.dangers.some(
            (d) => d.x === s.player.x && d.y === s.player.y
          );

          if (hit) {
            s.lives -= 1;
            if (s.lives <= 0) {
              s.phase = "gameover";
              setPhase("gameover");
            } else {
              s.phase = "dead";
              setPhase("dead");
              setTimeout(() => {
                s.player = { x: 1, y: 1, dir: [1, 0], mouth: 0 };
                s.dangers = [{ x: COLS - 2, y: ROWS - 2 }];
                s.phase = "playing";
                setPhase("playing");
                setHud(h => ({ ...h, lives: s.lives }));
              }, 900);
            }
            setHud({ score: s.score, lives: s.lives, foods: s.foods.length });
        }
      }

      draw(ctx, s);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // ── Draw ──────────────────────────────────────────────────────────────────
  const draw = (ctx, s) => {
    if (!s) return;
    ctx.clearRect(0, 0, W, H);

    // background
    ctx.fillStyle = "#05060f";
    ctx.fillRect(0, 0, W, H);

    // maze
    for (let y = 0; y < ROWS; y++) {
      for (let x = 0; x < COLS; x++) {
        if (MAZE_TEMPLATE[y][x] === 1) {
          // wall fill
          ctx.fillStyle = "#0e1a3a";
          ctx.fillRect(x * TILE, y * TILE, TILE, TILE);
          // glowing border
          ctx.strokeStyle = "#1e3fd0";
          ctx.lineWidth = 2;
          ctx.shadowColor = "#3a6fff";
          ctx.shadowBlur = 6;
          ctx.strokeRect(x * TILE + 1, y * TILE + 1, TILE - 2, TILE - 2);
          ctx.shadowBlur = 0;
        } else {
          // path dot
          ctx.fillStyle = "rgba(255,255,255,0.04)";
          ctx.fillRect(x * TILE, y * TILE, TILE, TILE);
          ctx.fillStyle = "rgba(255,255,255,0.25)";
          ctx.beginPath();
          ctx.arc(x * TILE + TILE / 2, y * TILE + TILE / 2, 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    // foods (image or emoji/circle)
    s.foods.forEach((f) => {
      const cx = f.x * TILE + TILE / 2;
      const cy = f.y * TILE + TILE / 2;

      // glow
      ctx.shadowColor = "#ffc94d";
      ctx.shadowBlur = 14;

      if (f.imageUrl) {
        const img = new Image();
        img.src = f.imageUrl;

        ctx.drawImage(
          img,
          f.x * TILE + 5,
          f.y * TILE + 5,
          TILE - 10,
          TILE - 10
        );
      } else {
        ctx.fillStyle = "rgba(255,200,50,0.15)";
        ctx.beginPath();
        ctx.arc(cx, cy, TILE / 2 - 4, 0, Math.PI * 2);
        ctx.fill();

        ctx.font = `${TILE - 10}px serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(f.emoji || "🍽️", cx, cy + 1);
      }

      ctx.shadowBlur = 0;
    });

    // dangers (ghosts)
    s.dangers.forEach((d) => {
      const gx = d.x * TILE;
      const gy = d.y * TILE;
      const gc = TILE / 2;

      ctx.shadowColor = "#ff3a3a";
      ctx.shadowBlur = 18;
      ctx.fillStyle = "#ff4040";

      ctx.beginPath();
      ctx.arc(gx + gc, gy + gc - 4, TILE / 2 - 5, Math.PI, 0, false);
      ctx.lineTo(gx + TILE - 5, gy + TILE - 5);
      ctx.lineTo(gx + TILE - 5 - (TILE - 10) / 3, gy + gc + 4);
      ctx.lineTo(gx + gc, gy + TILE - 5);
      ctx.lineTo(gx + 5 + (TILE - 10) / 3, gy + gc + 4);
      ctx.lineTo(gx + 5, gy + TILE - 5);
      ctx.closePath();
      ctx.fill();

      // 👁️ occhi (DEVONO stare qui dentro)
      ctx.fillStyle = "white";
      ctx.beginPath(); ctx.arc(gx + gc - 5, gy + gc - 4, 4, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(gx + gc + 5, gy + gc - 4, 4, 0, Math.PI * 2); ctx.fill();

      ctx.fillStyle = "#111";
      ctx.beginPath(); ctx.arc(gx + gc - 4, gy + gc - 4, 2, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(gx + gc + 6, gy + gc - 4, 2, 0, Math.PI * 2); ctx.fill();

      ctx.shadowBlur = 0;
    });

    // dead flash
    if (s.phase === "dead") {
      ctx.fillStyle = "rgba(255,60,60,0.35)";
      ctx.fillRect(0, 0, W, H);
    }

    // player (pac-man)
    const px = s.player.x * TILE;
    const py = s.player.y * TILE;
    const pc = TILE / 2;
    const angle = (Math.PI / 5) * (s.player.mouth < 3 ? s.player.mouth : 6 - s.player.mouth);

    // direction angle
    const [dx, dy] = s.player.dir || [1, 0];
    const facingAngle = Math.atan2(dy, dx);

    ctx.shadowColor = "#ffe033";
    ctx.shadowBlur = 16;
    ctx.fillStyle = "#ffe033";
    ctx.beginPath();
    ctx.moveTo(px + pc, py + pc);
    ctx.arc(px + pc, py + pc, TILE / 2 - 4, facingAngle + angle, facingAngle + Math.PI * 2 - angle, false);
    ctx.closePath();
    ctx.fill();
    ctx.shadowBlur = 0;

    // eye
    const eyeX = px + pc + Math.cos(facingAngle - Math.PI / 4) * (TILE / 4);
    const eyeY = py + pc + Math.sin(facingAngle - Math.PI / 4) * (TILE / 4);
    ctx.fillStyle = "#111";
    ctx.beginPath();
    ctx.arc(eyeX, eyeY, 2.5, 0, Math.PI * 2);
    ctx.fill();
  };

  // ── Restart ───────────────────────────────────────────────────────────────
  const restart = () => {
    stateRef.current = buildState();
    setHud({ score: 0, lives: 3, foods: stateRef.current.foods.length });
    setPhase("playing");
    setCollected(null);
  };

  // ── D-pad for mobile ──────────────────────────────────────────────────────
  const dpad = (dir) => {
    if (!stateRef.current || stateRef.current.phase !== "playing") return;
    stateRef.current.nextDir = dir;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={styles.root} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      {/* HUD */}
      <div style={styles.hud}>
        <div style={styles.hudItem}>
          <span style={styles.hudLabel}>PUNTI</span>
          <span style={styles.hudValue}>{hud.score}</span>
        </div>
        <div style={{ ...styles.hudItem, textAlign: "center" }}>
          <span style={styles.hudTitle}>🍽️ PiattoMan</span>
        </div>
        <div style={{ ...styles.hudItem, textAlign: "right" }}>
          <span style={styles.hudLabel}>VITE</span>
          <span style={styles.hudValue}>{"❤️".repeat(hud.lives)}</span>
        </div>
      </div>

      {/* Canvas */}
      <div style={styles.canvasWrap}>
        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          style={styles.canvas}
        />

        {/* Food popup */}
        {collected && (
          <div style={styles.popup}>
            <div style={styles.popupEmoji}>{collected.emoji}</div>
            <div style={styles.popupName}>{collected.nome}</div>
            {collected.prezzo && <div style={styles.popupPrice}>€{collected.prezzo}</div>}
            <div style={styles.popupPoints}>+10</div>
          </div>
        )}

        {/* Overlay: game over / win */}
        {(phase === "gameover" || phase === "win") && (
          <div style={styles.overlay}>
            <div style={styles.overlayBox}>
              <div style={styles.overlayEmoji}>{phase === "win" ? "🏆" : "💀"}</div>
              <div style={styles.overlayTitle}>
                {phase === "win" ? "Hai vinto!" : "Game Over"}
              </div>
              <div style={styles.overlayScore}>Punti: {hud.score}</div>
              <button style={styles.btn} onClick={restart}>🔄 Rigioca</button>
            </div>
          </div>
        )}
      </div>

      {/* D-pad */}
      <div style={styles.dpad}>
        <div style={styles.dpadRow}>
          <button style={styles.dBtn} onClick={() => dpad([0, -1])}>▲</button>
        </div>
        <div style={styles.dpadRow}>
          <button style={styles.dBtn} onClick={() => dpad([-1, 0])}>◀</button>
          <div style={styles.dBtnCenter} />
          <button style={styles.dBtn} onClick={() => dpad([1, 0])}>▶</button>
        </div>
        <div style={styles.dpadRow}>
          <button style={styles.dBtn} onClick={() => dpad([0, 1])}>▼</button>
        </div>
      </div>

      {onBack && (
        <button style={styles.backBtn} onClick={onBack}>← Torna ai giochi</button>
      )}
    </div>
  );
}

// ─── STYLES ────────────────────────────────────────────────────────────────
const styles = {
  root: {
    minHeight: "100vh",
    background: "#05060f",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Press Start 2P', monospace",
    padding: "1rem",
    gap: "0.75rem",
    userSelect: "none",
  },
  hud: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    maxWidth: `${W}px`,
    color: "#ffe033",
    padding: "0.5rem 1rem",
    background: "rgba(255,224,51,0.07)",
    borderRadius: "10px",
    border: "1px solid rgba(255,224,51,0.2)",
  },
  hudItem: { flex: 1, display: "flex", flexDirection: "column", gap: "2px" },
  hudLabel: { fontSize: "0.5rem", color: "rgba(255,224,51,0.6)", letterSpacing: "2px" },
  hudValue: { fontSize: "0.85rem" },
  hudTitle: { fontSize: "0.7rem", color: "#ffe033", textAlign: "center" },
  canvasWrap: {
    position: "relative",
    width: "100%",
    maxWidth: `${W}px`,
    aspectRatio: `${W} / ${H}`,
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 0 40px rgba(30,63,208,0.4), 0 0 80px rgba(30,63,208,0.15)",
    border: "2px solid #1e3fd0",
  },
  canvas: {
    display: "block",
    width: "100%",
    height: "100%",
    imageRendering: "pixelated",
  },
  popup: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    background: "rgba(10,12,30,0.92)",
    border: "2px solid #ffe033",
    borderRadius: "12px",
    padding: "1rem 1.5rem",
    textAlign: "center",
    color: "white",
    pointerEvents: "none",
    zIndex: 10,
    animation: "fadeUp 0.3s ease",
    boxShadow: "0 0 30px rgba(255,224,51,0.3)",
  },
  popupEmoji: { fontSize: "2rem", marginBottom: "4px" },
  popupName:  { fontSize: "0.6rem", color: "#ffe033", marginBottom: "2px" },
  popupPrice: { fontSize: "0.55rem", color: "#aaa" },
  popupPoints:{ fontSize: "0.7rem", color: "#7fff7f", marginTop: "4px" },
  overlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(5,6,15,0.85)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 20,
  },
  overlayBox: {
    textAlign: "center",
    padding: "2rem",
    background: "rgba(14,26,58,0.95)",
    borderRadius: "16px",
    border: "2px solid #1e3fd0",
    boxShadow: "0 0 40px rgba(30,63,208,0.5)",
    color: "white",
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
    alignItems: "center",
  },
  overlayEmoji: { fontSize: "3rem" },
  overlayTitle: { fontSize: "1rem", color: "#ffe033" },
  overlayScore: { fontSize: "0.65rem", color: "#aaa" },
  dpad: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "2px",
  },
  dpadRow: {
    display: "flex",
    gap: "2px",
    justifyContent: "center",
    alignItems: "center",
  },
  dBtn: {
    width: 52,
    height: 52,
    background: "rgba(255,224,51,0.1)",
    border: "1px solid rgba(255,224,51,0.3)",
    borderRadius: "8px",
    color: "#ffe033",
    fontSize: "1.2rem",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    touchAction: "manipulation",
  },
  dBtnCenter: { width: 52, height: 52 },
  btn: {
    padding: "0.7rem 1.4rem",
    background: "#ffe033",
    color: "#05060f",
    border: "none",
    borderRadius: "8px",
    fontFamily: "'Press Start 2P', monospace",
    fontSize: "0.6rem",
    cursor: "pointer",
    marginTop: "0.5rem",
  },
  backBtn: {
    padding: "0.6rem 1.2rem",
    background: "transparent",
    color: "rgba(255,224,51,0.6)",
    border: "1px solid rgba(255,224,51,0.3)",
    borderRadius: "8px",
    fontFamily: "'Press Start 2P', monospace",
    fontSize: "0.5rem",
    cursor: "pointer",
    marginTop: "0.25rem",
  },
};
